"use client";
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import StatusBadge from "@/components/dashboard/StatusBadge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { ChevronDown, ChevronRight, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

const liveSync = [
  { service: "Exchange", progress: 67, eta: "12 min" },
  { service: "SharePoint", progress: 45, eta: "22 min" },
  { service: "OneDrive", progress: 89, eta: "4 min" },
  { service: "Teams", progress: 31, eta: "28 min" },
];

const objectsByService = [
  { name: "Exchange", count: 520 },
  { name: "SharePoint", count: 340 },
  { name: "OneDrive", count: 287 },
  { name: "Teams", count: 100 },
];

const durationTrend = [
  { day: "Mon", duration: 42 },
  { day: "Tue", duration: 48 },
  { day: "Wed", duration: 51 },
  { day: "Thu", duration: 44 },
  { day: "Fri", duration: 47 },
  { day: "Sat", duration: 39 },
  { day: "Sun", duration: 41 },
];

const storagePie = [
  { name: "Exchange", value: 35, color: "hsl(210, 100%, 19%)" },
  { name: "SharePoint", value: 30, color: "hsl(210, 70%, 36%)" },
  { name: "OneDrive", value: 25, color: "hsl(183, 100%, 35%)" },
  { name: "Teams", value: 10, color: "hsl(210, 18%, 42%)" },
];

const logs = [
  { time: "14:32:01", service: "Exchange", action: "Sync mailbox batch 12/18", status: "success" as const, details: "Processed 42 mailboxes, 1,247 items. No errors." },
  { time: "14:31:45", service: "SharePoint", action: "Indexing site collection", status: "running" as const, details: "Crawling /sites/finance — 340 documents found." },
  { time: "14:31:12", service: "OneDrive", action: "Upload to S3", status: "success" as const, details: "Uploaded 287 files (0.8 GB) to eu-west-1 bucket." },
  { time: "14:30:58", service: "Exchange", action: "API throttled", status: "warning" as const, details: "Microsoft Graph rate limit hit. Retrying in 30s." },
  { time: "14:30:22", service: "Teams", action: "Channel export failed", status: "error" as const, details: "Error 403: Insufficient permissions for channel #general in Team 'Engineering'." },
];

const SyncMonitoring = () => {
  const [expandedLog, setExpandedLog] = useState<number | null>(null);
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">{t("syncMonitoring.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("syncMonitoring.subtitle")}</p>
      </div>

      <div className="rounded-lg border border-border/50 bg-card p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium">{t("syncMonitoring.liveProgress")}</h3>
          <Button variant="outline" size="sm" className="gap-1.5 text-amber-600 border-amber-200 hover:bg-amber-50 hover:text-amber-700">
            <XCircle className="h-3.5 w-3.5" /> {t("syncMonitoring.cancelSync")}
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {liveSync.map((s) => (
            <div key={s.service} className="space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="font-medium">{s.service}</span>
                <span className="text-muted-foreground text-xs">{t("syncMonitoring.eta")} {s.eta}</span>
              </div>
              <div className="flex items-center gap-3">
                <Progress value={s.progress} className="h-2 flex-1" />
                <span className="text-xs font-medium text-muted-foreground w-10 text-right">{s.progress}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-lg border border-border/50 bg-card p-5 shadow-sm">
          <h3 className="text-sm font-medium text-foreground mb-4">{t("syncMonitoring.objectsByService")}</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={objectsByService}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 20%, 91%)" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(215, 16%, 47%)" }} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(215, 16%, 47%)" }} />
              <Tooltip />
              <Bar dataKey="count" fill="hsl(210, 100%, 19%)" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-lg border border-border/50 bg-card p-5 shadow-sm">
          <h3 className="text-sm font-medium text-foreground mb-4">{t("syncMonitoring.durationTrend")}</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={durationTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 20%, 91%)" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: "hsl(215, 16%, 47%)" }} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(215, 16%, 47%)" }} unit="m" />
              <Tooltip />
              <Line type="monotone" dataKey="duration" stroke="hsl(210, 100%, 19%)" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-lg border border-border/50 bg-card p-5 shadow-sm">
          <h3 className="text-sm font-medium text-foreground mb-4">{t("syncMonitoring.storageByService")}</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={storagePie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} strokeWidth={1}>
                {storagePie.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-lg border border-border/50 bg-card shadow-sm">
        <div className="p-5 border-b border-border/50">
          <h3 className="text-sm font-medium">{t("syncMonitoring.detailedLogs")}</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs w-8"></TableHead>
              <TableHead className="text-xs">{t("syncMonitoring.time")}</TableHead>
              <TableHead className="text-xs">{t("syncMonitoring.service")}</TableHead>
              <TableHead className="text-xs">{t("syncMonitoring.action")}</TableHead>
              <TableHead className="text-xs">{t("syncMonitoring.status")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.map((log, i) => (
              <React.Fragment key={`${i}-log`}>
                <TableRow className="cursor-pointer" onClick={() => setExpandedLog(expandedLog === i ? null : i)}>
                  <TableCell className="py-2 px-3">
                    {expandedLog === i ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                  </TableCell>
                  <TableCell className="text-sm font-mono text-muted-foreground">{log.time}</TableCell>
                  <TableCell className="text-sm">{log.service}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{log.action}</TableCell>
                  <TableCell><StatusBadge status={log.status} /></TableCell>
                </TableRow>
                {expandedLog === i && (
                  <TableRow key={`${i}-detail`}>
                    <TableCell colSpan={5} className="bg-secondary/50 text-xs text-muted-foreground font-mono py-3 px-8">
                      {log.details}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default SyncMonitoring;
